import { PureComponent } from "react";

import { cn } from "@btnet/utilities-core";

import {
    recalculateAllFields,
    recalculateCostLineItemFields,
} from "utilities/lineItem/markupUtility";
import { getAccuracy } from "utilities/number/number";

import { BTFormItemAutomatic } from "commonComponents/btWrappers/BTForm/BTForm";
import {
    BTInputNumber,
    defaultAntStyle,
} from "commonComponents/btWrappers/BTInputNumber/BTInputNumber";
import { type IGridCellRenderProps } from "commonComponents/utilities/Grid/Grid.types";
import {
    type IPreviewLineItemProps,
    LineItemPreview,
} from "commonComponents/utilities/LineItemContainer/LineItemPreview";
import { GridLineItemColumnType } from "commonComponents/utilities/LineItemContainer/types/LineItem.enums";
import { type ILineItemColumn } from "commonComponents/utilities/LineItemContainer/types/LineItem.interfaces";
import {
    isCostLineItem,
    isMarkupLineItem,
    LineItemColumns,
    LineItemConstants,
} from "commonComponents/utilities/LineItemContainer/types/LineItem.types";
import { type IBaseLineItem } from "commonComponents/utilities/LineItemContainer/types/LineItem.types";

import { type JobTaxVersion, type TaxMethod } from "entity/tax/common/tax.types";

import "./CellImplementations.less";

export interface IQuantityCellProps extends IBaseLineItem, IPreviewLineItemProps {
    allowNull?: boolean;
    ownerPriceLocked?: boolean;
    ellipsis?: boolean;
    totalEffectiveRate?: number;
    effectiveTaxVersion?: JobTaxVersion;
    taxMethod?: TaxMethod;
    removeTrailingZerosOnEdit?: boolean;
}

export class QuantityCell extends PureComponent<IGridCellRenderProps<IQuantityCellProps>> {
    private handleChange = (fieldName: string, value?: number | null) => {
        const { cellValue: lineItem, onChange, field } = this.props;

        if (lineItem) {
            const isClientPriceLocked = lineItem.ownerPriceLocked;

            let quantity;
            if (lineItem.allowNull) {
                quantity = value ?? null;
            } else {
                quantity = value ?? 0;
            }

            if (isMarkupLineItem(lineItem)) {
                onChange(field.path, {
                    ...lineItem,
                    ...recalculateAllFields(
                        {
                            ...lineItem,
                            quantity: quantity ?? 0,
                        },
                        isClientPriceLocked,
                    ),
                    quantity,
                });
            } else if (isCostLineItem(lineItem)) {
                onChange(field.path, {
                    ...lineItem,
                    ...recalculateCostLineItemFields({
                        ...lineItem,
                        quantity: quantity ?? 0,
                    }),
                    quantity,
                });
            } else {
                onChange(fieldName, quantity);
            }
        }
    };

    private handleBlur = () => {
        const { field, rowIndex, setRowTouched } = this.props;

        setRowTouched!(rowIndex!, [field.name, "ownerPrice", "builderCost"], true);
    };

    private handleFocus(event: React.FocusEvent<HTMLInputElement>) {
        event.target.select();
    }

    render() {
        const { cellValue, isInEdit, field, canEdit, autoFocus, onFocus } = this.props;

        const canEditQuantity = canEdit;
        const readOnly = !isInEdit || !canEditQuantity;
        const decimalScale = cellValue?.removeTrailingZerosOnEdit
            ? Math.min(4, getAccuracy(cellValue?.quantity!))
            : 4;

        const component = (
            <LineItemPreview
                fieldName="quantity"
                addPreviewListener={cellValue?.addPreviewListener}
                render={(preview) => (
                    <BTInputNumber<undefined>
                        min={LineItemConstants.Quantity.MIN}
                        max={LineItemConstants.Quantity.MAX}
                        id={field.full}
                        data-testid={field.full}
                        value={preview?.quantity ?? cellValue?.quantity}
                        allowNull={cellValue?.allowNull}
                        readOnly={readOnly}
                        precision={decimalScale}
                        style={defaultAntStyle}
                        onChange={this.handleChange}
                        onBlur={this.handleBlur}
                        autoFocus={autoFocus}
                        onFocus={this.handleFocus}
                        className={preview ? "LineItemPreview" : undefined}
                        removeTrailingZerosOnEdit={cellValue?.removeTrailingZerosOnEdit}
                    />
                )}
            />
        );

        if (isInEdit) {
            return (
                <BTFormItemAutomatic
                    id={field.full}
                    className={cn(
                        "LineItemCell",
                        { RightAlignFormLabel: !canEditQuantity },
                        { ellipsis: cellValue?.ellipsis },
                    )}
                >
                    {component}
                </BTFormItemAutomatic>
            );
        }

        return (
            <div
                className={cn(
                    "LineItemCell",
                    { lineItemEditableCell: canEditQuantity },
                    { ellipsis: cellValue?.ellipsis },
                )}
                tabIndex={canEditQuantity ? 0 : undefined}
                onFocus={onFocus}
            >
                {component}
            </div>
        );
    }
}

export const quantityCellDefaultConfig = <T extends IBaseLineItem>() =>
    ({
        id: LineItemColumns.Quantity,
        name: "Quantity",
        jsonKey: "quantity",
        width: 100.0,
        minWidth: 48,
        ellipsis: true,
        align: "right",
        isSortable: true,
        columnType: GridLineItemColumnType.flatNumber,
        isFrozen: false,
        isHidden: false,
        Cell: QuantityCell,
    }) as Partial<ILineItemColumn<T, IQuantityCellProps>>;
